import * as v from "valibot";
import { ContestStatus, convertContestStatus } from "./contestStatus";
import {
  query_legal_agreement as RpcQueryLegalAgreement,
  query_recursive_types as RpcRecursiveTypes,
} from "@/infra/api/rpc/api";
import { filterUndefined } from "@/utils/filter-undefined";

type ContestResponse = RpcRecursiveTypes.IContestResponse;
type ContestLegalAgreementResponse =
  RpcQueryLegalAgreement.ILegalAgreementResponse;

export const ContestLegalAgreementSchema = v.object({
  id: v.string(),
  name: v.string(),
  latestVersion: v.object({
    id: v.string(),
    descriptionHtml: v.string(),
    userAccepted: v.boolean(),
  }),
});

export type IContestLegalAgreement = v.InferOutput<
  typeof ContestLegalAgreementSchema
>;

export const ContestDetailSchema = v.object({
  id: v.string(),
  status: v.enum(ContestStatus),
  name: v.string(),
  thumbnailUrl: v.optional(v.string()),
  legalAgreement: ContestLegalAgreementSchema,
  // ユーザーがコンテストに応募している全作品のIDのリスト
  userEntrySeriesIds: v.array(v.string()),
});

export type IContestDetail = v.InferOutput<typeof ContestDetailSchema>;

const convertContestLegalAgreementResponseToIContestLegalAgreement = (
  data: ContestLegalAgreementResponse,
): IContestLegalAgreement => {
  return v.parse(ContestLegalAgreementSchema, {
    id: data.id?.value,
    name: data.name?.value ?? "",
    latestVersion: {
      id: data.latestVersion?.versionId?.value,
      descriptionHtml: data.latestVersion?.descriptionHtml?.value ?? "",
      userAccepted: data.latestVersion?.userAccepted?.value ?? false,
    },
  });
};

const convertContestLegalAgreementResponseToIContestLegalAgreementOrUndefined =
  (data: ContestLegalAgreementResponse): IContestLegalAgreement | undefined => {
    try {
      return convertContestLegalAgreementResponseToIContestLegalAgreement(data);
    } catch (error) {
      console.error("IContestLegalAgreement convert error:", error);
      return;
    }
  };

/**
 * @throws {v.ValiError}
 */
export const convertContestResponseToIContestDetail = (
  data: ContestResponse,
): IContestDetail => {
  const legalAgreement = data.legalAgreement
    ? convertContestLegalAgreementResponseToIContestLegalAgreementOrUndefined(
        data.legalAgreement,
      )
    : undefined;

  return v.parse(ContestDetailSchema, {
    id: data.id?.value,
    status: convertContestStatus(data.status),
    name: data.name?.value ?? "",
    description: data.description?.value ?? "",
    thumbnailImageUrl: data.thumbnail?.servingUrl?.value ?? undefined,
    legalAgreement: legalAgreement,
    userEntrySeriesIds:
      data.userEntrySeriesList
        ?.map((item) => item.id?.value)
        .filter(filterUndefined) ?? [],
  });
};

export const convertContestResponseToIContestDetailOrUndefined = (
  data: ContestResponse,
): IContestDetail | undefined => {
  try {
    return convertContestResponseToIContestDetail(data);
  } catch (error) {
    console.error("IContestDetail convert error:", error);
    return;
  }
};
